/**
 * /admin/intl-couriers — moderation console for international couriers.
 *
 *   1. Applications queue — pending_review apps with Approve / Reject
 *   2. Courier roster — every courier's info, credits, Pro sub, lead
 *      stats + one-click Suspend / Reactivate
 *   3. Recent leads — buyer inquiries with delivery status
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ArrowLeftIcon as ArrowLeft,
  AirplaneTiltIcon as AirplaneTilt,
  ShieldCheckIcon as ShieldCheck,
  EnvelopeIcon as Envelope,
  CaretDownIcon as CaretDown,
  CaretUpIcon as CaretUp,
} from "@phosphor-icons/react";

const STATUS_COLOR = {
  active: "#0F8043",
  suspended: "#C1272D",
  pending_review: "#C9A227",
  pending_payment: "#9A9A9A",
};

const Pill = ({ label, color }) => (
  <span
    className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
    style={{ background: `${color}18`, color }}
  >
    {label}
  </span>
);

const SectionHeader = ({ icon: Icon, label, color, count }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
      <Icon size={18} weight="duotone" />
    </span>
    <h2 className="font-spectral text-2xl tracking-tight">{label}</h2>
    {count != null && <span className="text-[12px] text-[#9A9A9A]">({count})</span>}
  </div>
);

function FunnelSection() {
  const [funnel, setFunnel] = useState(null);

  useEffect(() => {
    api.get("/admin/transport/international/funnel")
      .then((r) => setFunnel(r.data?.funnel || []))
      .catch(() => setFunnel([]));
  }, []);

  if (!funnel) return null;
  const max = Math.max(1, ...funnel.map((s) => s.count));
  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6 mb-5" data-testid="intl-couriers-funnel">
      <SectionHeader icon={AirplaneTilt} label="Conversion funnel" color="#C1272D" />
      <div className="space-y-2.5">
        {funnel.map((s, i) => {
          const prev = i > 0 ? funnel[i - 1].count : null;
          const conv = prev ? Math.round((s.count / Math.max(1, prev)) * 100) : null;
          return (
            <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-step-${s.key}`}>
              <div className="w-44 text-[12px] text-[#3a3a3a] font-medium flex-shrink-0">{s.label}</div>
              <div className="flex-1 h-6 bg-[#FBF7EC] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${Math.max(3, (s.count / max) * 100)}%`, background: "#C9A227" }}
                />
              </div>
              <div className="w-20 text-right text-[12px] tabular-nums flex-shrink-0">
                <strong>{s.count}</strong>
                {conv != null && <span className="text-[#9A9A9A] ml-1">({conv}%)</span>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-[#9A9A9A] mt-3">
        Each % is conversion from the previous step. "Approved" includes admin-seeded couriers who never applied.
      </p>
    </section>
  );
}

function ApplicationsSection({ onChanged }) {
  const [apps, setApps] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/transport/international/applications")
      .then((r) => setApps((r.data?.applications || []).filter(
        (a) => !["approved", "rejected"].includes(a.status)
      )))
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/transport/international/applications/${id}/approve`);
      toast.success("Approved — courier is live. Free lead credit granted + approval email sent.");
      load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed.");
    } finally { setBusyId(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Rejection reason (sent nowhere, stored for audit):") || "";
    setBusyId(id);
    try {
      await api.post(`/admin/transport/international/applications/${id}/reject`, { reason });
      toast.success("Application rejected.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed.");
    } finally { setBusyId(null); }
  };

  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6 mb-5" data-testid="intl-couriers-applications">
      <SectionHeader icon={Envelope} label="Applications queue" color="#C9A227" count={apps.length} />
      {apps.length === 0 ? (
        <p className="text-[13px] text-[#9A9A9A]">No pending applications.</p>
      ) : (
        <div className="space-y-3">
          {apps.map((a) => (
            <div key={a.id} className="border border-[#F0EAD6] rounded-xl p-4" data-testid={`intl-app-${a.id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <strong className="text-[14px]">{a.display_name}</strong>
                <Pill label={a.status.replaceAll("_", " ")} color={STATUS_COLOR[a.status] || "#9A9A9A"} />
                {a.application_fee_paid && <Pill label="$9 paid" color="#0F8043" />}
              </div>
              <div className="text-[12px] text-[#6B6B6B]">
                {a.email} · {a.home_country} · {(a.corridors || []).join(", ") || "no corridors"}
              </div>
              <div className="text-[11px] text-[#9A9A9A] mt-1">
                {a.years_in_business} yrs · ~{a.trips_completed_estimate} trips
                {a.ipata_member_number && <> · IPATA {a.ipata_member_number}</>}
                {a.cdc_dog_import_permit_number && <> · CDC {a.cdc_dog_import_permit_number}</>}
                {a.insurance_carrier && <> · Ins: {a.insurance_carrier} (${(a.insurance_liability_usd || 0).toLocaleString()})</>}
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  disabled={busyId === a.id}
                  onClick={() => approve(a.id)}
                  className="rounded-full bg-[#0F8043] hover:bg-ink text-white text-[12px] h-8"
                  data-testid={`intl-app-approve-${a.id}`}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === a.id}
                  onClick={() => reject(a.id)}
                  className="rounded-full text-[12px] h-8"
                  data-testid={`intl-app-reject-${a.id}`}
                >
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CourierRow({ c, onAction }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    if (action === "suspend" && !window.confirm(`Suspend ${c.display_name}? They disappear from the public directory immediately.`)) return;
    const reason = action === "suspend" ? (window.prompt("Suspension reason (stored for audit):") || "") : null;
    setBusy(true);
    try {
      await api.post(`/admin/transport/international/couriers/${c.id}/${action}`, reason != null ? { reason } : {});
      toast.success(action === "suspend" ? "Courier suspended." : "Courier reactivated.");
      onAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#F0EAD6] rounded-xl p-4" data-testid={`intl-courier-${c.slug}`}>
      <div className="flex flex-wrap items-center gap-2">
        <strong className="text-[14px]">{c.display_name}</strong>
        <Pill label={c.status} color={STATUS_COLOR[c.status] || "#9A9A9A"} />
        {c.pro_sub_active && <Pill label={`Pro · ${c.pro_sub_plan}`} color="#0F8043" />}
        {!c.has_account && <Pill label="No account yet" color="#C9A227" />}
        <span className="ml-auto text-[11px] text-[#9A9A9A]">
          {c.credits} credit{c.credits !== 1 ? "s" : ""} · {c.leads_delivered}/{c.leads_total} leads delivered
        </span>
      </div>
      <div className="text-[12px] text-[#6B6B6B] mt-1">
        {c.email} · {c.home_country} · {(c.corridors || []).join(", ") || "no corridors"}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] text-crimson hover:text-ink inline-flex items-center gap-1"
          data-testid={`intl-courier-toggle-${c.slug}`}
        >
          {open ? <CaretUp size={11} /> : <CaretDown size={11} />} {open ? "Hide details" : "Full details"}
        </button>
        <Link to={`/transport/international/${c.slug}`} className="text-[11px] text-[#6B6B6B] hover:text-ink underline-offset-2 hover:underline">
          Public profile →
        </Link>
        <span className="flex-1" />
        {c.status === "active" ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act("suspend")} className="rounded-full text-[11px] h-7 text-[#C1272D] border-[#E8C0C2]" data-testid={`intl-courier-suspend-${c.slug}`}>
            Suspend
          </Button>
        ) : (
          <Button size="sm" disabled={busy} onClick={() => act("reactivate")} className="rounded-full text-[11px] h-7 bg-[#0F8043] hover:bg-ink text-white" data-testid={`intl-courier-reactivate-${c.slug}`}>
            Reactivate
          </Button>
        )}
      </div>
      {open && (
        <div className="mt-3 bg-[#FBF7EC] border border-[#F0EAD6] rounded-lg p-3 text-[12px] text-[#3a3a3a] grid sm:grid-cols-2 gap-x-6 gap-y-1">
          <div><strong>Phone:</strong> {c.phone || "—"}</div>
          <div><strong>Legal name:</strong> {c.legal_name || "—"}</div>
          <div><strong>IPATA #:</strong> {c.ipata_member_number || "—"} {c.ipata_member_number_verified && "✓"}</div>
          <div><strong>CDC permit:</strong> {c.cdc_dog_import_permit_number || "—"} {c.cdc_permit_verified && "✓"}</div>
          <div><strong>USDA APHIS:</strong> {c.usda_aphis_importer_number || "—"} {c.usda_aphis_verified && "✓"}</div>
          <div><strong>IATA LAR:</strong> {c.iata_lar_certified ? "Certified" : "—"}</div>
          <div><strong>Insurance:</strong> {c.insurance_carrier || "—"} {c.insurance_liability_usd ? `($${c.insurance_liability_usd.toLocaleString()})` : ""}</div>
          <div><strong>Airlines:</strong> {(c.airlines_approved || []).join(", ") || "—"}</div>
          <div><strong>Customs broker:</strong> {c.customs_broker_contact || "—"}</div>
          <div><strong>Website:</strong> {c.website_url ? <a href={c.website_url} target="_blank" rel="noreferrer" className="text-crimson hover:underline">{c.website_url}</a> : "—"}</div>
          {c.insurance_certificate_url && (
            <div className="sm:col-span-2"><strong>Insurance cert:</strong> <a href={c.insurance_certificate_url} target="_blank" rel="noreferrer" className="text-crimson hover:underline">{c.insurance_certificate_url}</a></div>
          )}
          {c.suspension_reason && (
            <div className="sm:col-span-2 text-[#C1272D]"><strong>Suspension reason:</strong> {c.suspension_reason}</div>
          )}
          <div className="sm:col-span-2 text-[#9A9A9A]">Joined {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}{c.seeded_by_admin && " · seeded by admin"}</div>
        </div>
      )}
    </div>
  );
}

function RosterSection({ refreshKey, onAction }) {
  const [couriers, setCouriers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/transport/international/couriers")
      .then((r) => setCouriers(r.data?.couriers || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [refreshKey]);

  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6 mb-5" data-testid="intl-couriers-roster">
      <SectionHeader icon={ShieldCheck} label="Courier roster" color="#0F8043" count={couriers.length} />
      {!loaded ? (
        <div className="h-16 bg-[#FBF7EC] rounded-xl animate-pulse" />
      ) : couriers.length === 0 ? (
        <p className="text-[13px] text-[#9A9A9A]">No couriers yet — approve an application or seed one.</p>
      ) : (
        <div className="space-y-3">
          {couriers.map((c) => <CourierRow key={c.id} c={c} onAction={onAction} />)}
        </div>
      )}
    </section>
  );
}

function LeadsSection() {
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    api.get("/admin/transport/international/leads?limit=50")
      .then((r) => setLeads(r.data?.leads || []))
      .catch(() => {});
  }, []);

  return (
    <section className="rounded-2xl border border-[#EAEAEA] bg-white p-6 mb-5" data-testid="intl-couriers-leads">
      <SectionHeader icon={Envelope} label="Recent leads" color="#C9A227" count={leads.length} />
      {leads.length === 0 ? (
        <p className="text-[13px] text-[#9A9A9A]">No buyer inquiries yet.</p>
      ) : (
        <div className="space-y-2">
          {leads.map((l) => (
            <div key={l.id} className="text-[12px] py-2 border-b border-[#F0EAD6] last:border-none flex flex-wrap items-center gap-2">
              <strong>{l.origin_country} → {l.destination_country}</strong>
              <span className="text-[#6B6B6B]">{l.buyer_name} ({l.buyer_email})</span>
              <span className="text-[#9A9A9A]">→ {l.intl_transporter_slug}</span>
              <span className="ml-auto">
                {l.lead_delivered_via_email
                  ? <Pill label={`delivered · ${l.lead_funded_by || ""}`} color="#0F8043" />
                  : <Pill label={l.buyer_intro_fee_paid ? "queued" : "awaiting $9 fee"} color="#C9A227" />}
              </span>
              <span className="text-[10px] text-[#9A9A9A]">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AdminIntlCouriers() {
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);
  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-intl-couriers-page">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-[#6B6B6B] hover:text-ink text-sm mb-4" data-testid="back-to-admin">
          <ArrowLeft size={14} weight="bold" /> Back to admin
        </Link>
        <header className="mb-8 pb-6 border-b border-[#E5E5E5]">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#6B6B6B] font-bold mb-2">
            Roster · Applications · Moderation · Leads
          </p>
          <h1 className="font-spectral text-3xl sm:text-4xl text-ink flex items-center gap-3">
            <AirplaneTilt size={32} weight="duotone" className="text-[#C9A227]" /> International Couriers
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
            Every courier's credentials, credits, and lead history in one place.
            Pricing: $9 verification (first lead free) · $29 per lead · $199/mo Pro.
          </p>
        </header>

        <FunnelSection />
        <ApplicationsSection onChanged={bump} />
        <RosterSection refreshKey={refreshKey} onAction={bump} />
        <LeadsSection />
      </div>
    </AppShell>
  );
}
